// ─────────────────────────────────────────────────────────────────────────────
// userTier.js
//
// Source of truth for "which tier is this user?" Reads from Firebase Auth
// (to know who is signed in) and Firestore (to know what tier they have).
//
// Data model:
//   users/{uid}  -->  { email, tier, createdAt, lastSignInAt }
//   tier ∈ { 'free', 'artist', 'studio' }
//
// Signed-out users are always 'free'.
//
// To manually grant a paid tier to a user (until Stripe is wired up):
//   1. Have them sign in at least once so their users/{uid} doc exists.
//   2. In Firebase Console → Firestore → users → {their uid} → edit
//      the 'tier' field to 'artist' or 'studio'.
//   3. Their app refreshes the tier automatically via onSnapshot.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";

const TIERS = {
  FREE:   "free",
  ARTIST: "artist",
  STUDIO: "studio",
};

const VALID_TIERS = new Set(Object.values(TIERS));

const CAPABILITIES = {
  // TIFF export by resolution
  saveTiff_2k:   TIERS.ARTIST,
  saveTiff_4k:   TIERS.ARTIST,
  saveTiff_8k:   TIERS.STUDIO,
  saveTiff_12k:  TIERS.STUDIO,

  // PNG export by resolution.  "Canvas" (the on-screen render resolution,
  // ~1500x1000) is available to everyone — free users get a watermark.
  // Higher-resolution PNGs require re-rendering and are gated by tier.
  savePng_canvas: TIERS.FREE,
  savePng_2k:     TIERS.ARTIST,
  savePng_4k:     TIERS.ARTIST,
  savePng_8k:     TIERS.STUDIO,
  savePng_12k:    TIERS.STUDIO,
  instrumentalMusic: TIERS.ARTIST,
  bandComposer: TIERS.ARTIST,
  contourGlide: TIERS.STUDIO,
};

const TIER_RANK = {
  [TIERS.FREE]:   0,
  [TIERS.ARTIST]: 1,
  [TIERS.STUDIO]: 2,
};

// ─── Dev-only tier override ──────────────────────────────────────────────────
// In development builds, the URL query parameter ?devTier=free|artist|studio
// overrides whatever tier the Firestore document reports.  Useful for testing
// tier-gated features (PNG/TIFF resolutions, watermark, etc.) without having
// to flip the user's Firestore doc back and forth.
//
// Inert in production builds: process.env.NODE_ENV is "production" then, and
// the override is unconditionally null.
const DEV_TIER_OVERRIDE = (() => {
  if (process.env.NODE_ENV !== "development") return null;
  if (typeof window === "undefined") return null;
  const t = new URLSearchParams(window.location.search).get("devTier");
  if (t && VALID_TIERS.has(t)) {
    console.warn(`[userTier] DEV override active: tier=${t}`);
    return t;
  }
  return null;
})();

// Returns the effective tier: the dev override (if any) or the live Firestore
// value.  All public readers go through this so the override applies uniformly.
function effectiveTier() {
  return DEV_TIER_OVERRIDE ?? currentTier;
}


// ─── Module-level state ─────────────────────────────────────────────────────
//
// One Auth listener + one Firestore subscription serve every React component
// via useUserTier() / useCurrentUser().

let currentUser = null;
let currentTier = TIERS.FREE;
let userDocUnsub = null;
const listeners = new Set();

function notifyListeners() {
  const tier = effectiveTier();
  for (const fn of listeners) fn({ user: currentUser, tier });
}

// Read the signed `tier` custom claim from the user's ID token. This is the
// unforgeable source of truth: the claim is set server-side by the Stripe
// Cloud Function (Admin SDK) and signed by Firebase, so the browser can read
// it but cannot alter it. Anything other than a known paid tier → free.
async function applyTierFromClaim(user) {
  try {
    const { claims } = await user.getIdTokenResult();
    const t = claims.tier;
    currentTier = (t && VALID_TIERS.has(t)) ? t : TIERS.FREE;
  } catch (err) {
    console.warn("userTier: failed to read tier claim:", err);
    currentTier = TIERS.FREE; // fail safe
  }
  notifyListeners();
}

onAuthStateChanged(auth, async (user) => {
  if (userDocUnsub) {
    userDocUnsub();
    userDocUnsub = null;
  }

  currentUser = user;

  if (!user) {
    currentTier = TIERS.FREE;
    notifyListeners();
    return;
  }

  // Fail-safe: assume free immediately, then upgrade once the claim resolves.
  // (Worst case is a paid user briefly sees free controls — never the reverse.)
  currentTier = TIERS.FREE;
  notifyListeners();
  await applyTierFromClaim(user);

  // Keep the profile doc fresh (email / lastSignInAt / createdAt). We do NOT
  // write `tier` here — the Cloud Function owns it, and the Firestore rules
  // forbid clients from writing it. The doc's `tier` is only a server-written
  // mirror for display; enforcement reads the claim above.
  const userRef = doc(db, "users", user.uid);
  try {
    await setDoc(
      userRef,
      { email: user.email, lastSignInAt: serverTimestamp() },
      { merge: true }
    );
    await setDoc(
      userRef,
      { createdAt: serverTimestamp() },
      { merge: true, mergeFields: ["createdAt"] }
    );
  } catch (err) {
    console.warn("userTier: failed to upsert profile doc:", err);
  }

  // Watch the mirrored doc as a SIGNAL only: when the Cloud Function updates
  // the tier (e.g. right after a Stripe upgrade), `tierUpdatedAt` changes; we
  // force a token refresh so the new claim loads without requiring re-login,
  // then re-read the claim. The first snapshot is skipped (claim already read).
  let initialized = false;
  let lastSignal = 0;
  userDocUnsub = onSnapshot(
    userRef,
    async (snap) => {
      const sig = snap.data()?.tierUpdatedAt?.toMillis?.() ?? 0;
      if (!initialized) { initialized = true; lastSignal = sig; return; }
      if (sig === lastSignal) return;
      lastSignal = sig;
      try {
        await user.getIdToken(true);     // force refresh → pull latest claim
        await applyTierFromClaim(user);
      } catch (err) {
        console.warn("userTier: refresh after tier change failed:", err);
      }
    },
    (err) => {
      console.warn("userTier: snapshot error:", err);
    }
  );
});


// ─── Public API ─────────────────────────────────────────────────────────────

export function getUserTier() {
  return currentTier;
}

export function getCurrentUser() {
  return currentUser;
}

/**
 * Force-refresh the ID token and re-read the tier claim. Call this right after
 * the user returns from a successful Stripe checkout so the new tier applies
 * immediately instead of on the next (up to ~1h) automatic token refresh.
 * Returns the effective tier after refreshing.
 */
export async function refreshTier() {
  const user = auth.currentUser;
  if (!user) return TIERS.FREE;
  try {
    await user.getIdToken(true);
    await applyTierFromClaim(user);
  } catch (err) {
    console.warn("userTier: refreshTier failed:", err);
  }
  return effectiveTier();
}

/**
 * Returns the user's current tier. Re-renders the component when it changes.
 * API preserved from the previous version so callsites don't need to change.
 */
export function useUserTier() {
  const [tier, setTier] = useState(effectiveTier());
  useEffect(() => {
    const listener = (state) => setTier(state.tier);
    listeners.add(listener);
    setTier(effectiveTier());
    return () => { listeners.delete(listener); };
  }, []);
  return tier;
}

/**
 * Returns the currently signed-in user (or null). Re-renders on sign-in/out.
 */
export function useCurrentUser() {
  const [user, setUser] = useState(currentUser);
  useEffect(() => {
    const listener = (state) => setUser(state.user);
    listeners.add(listener);
    setUser(currentUser);
    return () => { listeners.delete(listener); };
  }, []);
  return user;
}

export function can(capability, tierOverride) {
  const required = CAPABILITIES[capability];
  if (!required) {
    console.warn(`can(): unknown capability "${capability}"`);
    return false;
  }
  const userTier = tierOverride ?? effectiveTier();
  return TIER_RANK[userTier] >= TIER_RANK[required];
}

export function tierRequiredFor(capability) {
  return CAPABILITIES[capability] ?? null;
}

export { TIERS };
